/**
 * Masthead behaviour for sections/nasty-g-header.liquid, ported from the global site's
 * Navigation.tsx.
 *
 * Four jobs, and deliberately only four.
 *
 * 1. The scroll state. One passive listener flips `data-scrolled` past the reference's 8px
 *    threshold, and every part of the morph (background, blur, edge, shadow, inline padding) is a
 *    CSS transition off that one attribute.
 *
 * 2. The chrome height. `.nasty-g-hero` and the locator's sticky bar both clear the masthead with
 *    `var(--header-height)`, whose 60px default comes from `sections/header.liquid`. That file is no
 *    longer rendered, so nothing measures the masthead any more and this does, into
 *    `--ngh-chrome-h`. The section stylesheet already authors the real heights as fallbacks, so
 *    this only has to catch a wrap the authored value did not predict: a longer compliance line, a
 *    zoomed page, a font that resolves wider.
 *
 * 3. The drawer, with the accessibility the reference does not have. Navigation.tsx has no
 *    `role="dialog"`, no `aria-modal`, no `aria-expanded`, no focus trap and no Escape handler: it
 *    is a `translate-x` panel and a click-to-close backdrop. This adds the trigger state, moves
 *    focus in and puts it back on the trigger afterwards, contains Tab while open, closes on
 *    Escape, and closes on a resize up into the desktop layout so the panel can never be left open
 *    behind a nav that no longer has a way to close it.
 *
 * 4. Two progressive upgrades. Search and cart ship as plain links to `routes.search_url` and
 *    `routes.cart_url` so they work with this file blocked or failed. When the theme's own search
 *    modal or cart drawer is present on the page, the click opens that instead, which is what the
 *    rest of the storefront does.
 *
 * The sibling dimming on the desktop links is not here. The reference tracks it in React state from
 * mouse events only; as a CSS `:hover`/`:focus-within` pair it costs no script and also fires for
 * someone arriving by Tab.
 */

const SCROLL_THRESHOLD = 8;

const DESKTOP = matchMedia('(min-width: 768px)');

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

class NastyHeader extends HTMLElement {
  connectedCallback() {
    this.chrome = this.querySelector('[data-header-chrome]');
    this.drawer = this.querySelector('[data-header-drawer]');
    this.trigger = this.querySelector('[data-header-trigger]');
    this.scrim = this.querySelector('[data-header-scrim]');

    this.open = false;
    this.returnFocus = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    this.watchScroll();

    this.measure();

    if (this.chrome && 'ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.chrome);
    }

    if (this.drawer && this.trigger) {
      this.trigger.addEventListener('click', () => this.openDrawer());
      this.scrim?.addEventListener('click', () => this.closeDrawer());

      for (const close of this.querySelectorAll('[data-header-close]')) {
        close.addEventListener('click', () => this.closeDrawer());
      }

      /*
       * A tap on any drawer link navigates, and on a same-page anchor it would otherwise leave the
       * panel sitting open over the destination.
       */
      for (const link of this.drawer.querySelectorAll('a[href]')) {
        link.addEventListener('click', () => this.closeDrawer());
      }

      this.drawer.addEventListener('keydown', (event) => this.trapFocus(event));
      document.addEventListener('keydown', this.onKeydown);
      DESKTOP.addEventListener('change', this.onBreakpoint);
    }

    for (const search of this.querySelectorAll('[data-header-search]')) {
      search.addEventListener('click', (event) => this.openSearch(event));
    }

    this.querySelector('[data-header-cart]')?.addEventListener('click', (event) => this.openCart(event));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    DESKTOP.removeEventListener('change', this.onBreakpoint);
    this.observer?.disconnect();
    this.scrollWatcher?.disconnect();
    this.sentinel?.remove();

    if (this.open) this.unlockScroll();
  }

  /**
   * Detect "scrolled past the threshold" without knowing which element scrolls.
   *
   * This started as `addEventListener('scroll')` reading `window.scrollY`, and it never fired on
   * desktop: Horizon scrolls `.page-wrapper` there and only the document at mobile, so `scrollY`
   * stayed at 0 and the bar never became opaque. Content then scrolled visibly through a
   * transparent masthead. A scroll listener would have to guess the right target at every
   * breakpoint; an IntersectionObserver on a sentinel does not care which ancestor moved.
   *
   * The sentinel is a threshold-tall block at the very top of the content, with an equal negative
   * bottom margin so it occupies no layout of its own.
   *
   * `flex:none` is not decoration. Its host is `.page-wrapper`, which Horizon lays out as a flex
   * column with `min-height: 100dvh`, so the sentinel is a flex item, and a flex item with no
   * content and the default `flex-shrink: 1` gets shrunk to zero on any page taller than the
   * viewport. The height then measured 0 while the negative margin still applied, so instead of
   * cancelling itself the sentinel pulled the entire document up by 9px: every template's first
   * section sat that far under the fixed masthead, and the threshold it exists to measure was gone
   * too. Pinning the basis keeps both halves of the trick honest.
   */
  watchScroll() {
    const host =
      document.querySelector('.page-wrapper') ||
      document.querySelector('#MainContent') ||
      document.body;

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText =
      `height:${SCROLL_THRESHOLD + 1}px;margin-bottom:-${SCROLL_THRESHOLD + 1}px;` +
      'flex:none;pointer-events:none;';
    host.prepend(sentinel);
    this.sentinel = sentinel;

    this.scrollWatcher = new IntersectionObserver(
      ([entry]) => this.toggleAttribute('data-scrolled', !entry.isIntersecting),
      { threshold: 0 }
    );
    this.scrollWatcher.observe(sentinel);
  }

  measure() {
    if (!this.chrome) return;
    document.body.style.setProperty('--ngh-chrome-h', `${this.chrome.offsetHeight}px`);
  }

  openDrawer() {
    if (this.open || !this.drawer) return;

    this.open = true;
    /*
     * `document.body` is the active element whenever nothing is focused, and focusing the body puts
     * focus nowhere, so the trigger is the fallback: it is where a reader who opened the panel came
     * from, and it is where the panel is reopened from.
     */
    const active = document.activeElement;
    this.returnFocus = active instanceof HTMLElement && active !== document.body ? active : this.trigger;

    this.dataset.drawer = 'open';
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.drawer.removeAttribute('inert');
    this.lockScroll();

    /*
     * The panel is `visibility: hidden` until the open attribute lands, and nothing inside a hidden
     * subtree can take focus, so the move waits one frame for that style to apply.
     */
    requestAnimationFrame(() => {
      const target = this.drawer.querySelector('[data-header-close]') ?? this.focusable()[0];
      target?.focus();
    });
  }

  closeDrawer() {
    if (!this.open || !this.drawer) return;

    this.open = false;
    this.dataset.drawer = 'closed';
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.drawer.setAttribute('inert', '');
    this.unlockScroll();

    this.returnFocus?.focus();
    this.returnFocus = null;
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      this.closeDrawer();
    }
  }

  onBreakpoint(event) {
    if (event.matches) this.closeDrawer();
  }

  focusable() {
    return Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
      (element) => element.offsetWidth > 0 || element.offsetHeight > 0
    );
  }

  trapFocus(event) {
    if (event.key !== 'Tab' || !this.open) return;

    const items = this.focusable();
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.drawer.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    this.scrollLocked = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.overflow = this.scrollLocked ?? '';
  }

  /**
   * Horizon renders `#search-modal` on every page from `layout/theme.liquid`. When it is there and
   * upgraded, open it; otherwise let the link go to the search page.
   */
  openSearch(event) {
    const modal = document.getElementById('search-modal');
    if (!modal) return;

    customElements.upgrade(modal);
    if (typeof modal.showDialog !== 'function') return;

    event.preventDefault();
    this.closeDrawer();
    modal.showDialog();
  }

  /**
   * `#cart-drawer` exists only when the cart type is set to drawer and the reader is not already on
   * the cart page, so an absent element is a normal state, not a failure.
   */
  openCart(event) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    customElements.upgrade(drawer);
    if (typeof drawer.toggle !== 'function') return;

    event.preventDefault();
    drawer.toggle();
  }
}

if (!customElements.get('nasty-header')) {
  customElements.define('nasty-header', NastyHeader);
}
